import {
  Battery,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  Power,
  PowerOff,
  Lock,
  AlertTriangle,
  Plug,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DeviceTelemetry } from '@/lib/machineStatus';

interface Props {
  telemetry: DeviceTelemetry;
  className?: string;
}

/**
 * Linha compacta de ícones complementares lendo `position.attributes`.
 * Cada ícone só aparece quando o atributo correspondente existe na position.
 */
export function TelemetryIcons({ telemetry, className }: Props) {
  const items: { key: string; node: React.ReactNode; tip: string }[] = [];

  if (telemetry.ignition !== undefined) {
    const Icon = telemetry.ignition ? Power : PowerOff;
    items.push({
      key: 'ignition',
      tip: telemetry.ignition ? 'Ignição ligada' : 'Ignição desligada',
      node: (
        <Icon
          className={cn(
            'h-3.5 w-3.5',
            telemetry.ignition ? 'text-status-running' : 'text-status-off',
          )}
        />
      ),
    });
  }

  if (telemetry.batteryLevel !== undefined) {
    const lvl = telemetry.batteryLevel;
    const Icon = lvl >= 70 ? BatteryFull : lvl >= 30 ? BatteryMedium : lvl > 0 ? BatteryLow : Battery;
    const color =
      lvl >= 70 ? 'text-status-running' : lvl >= 30 ? 'text-status-ready' : 'text-status-blocked';
    items.push({
      key: 'battery',
      tip: `Bateria interna ${Math.round(lvl)}%`,
      node: (
        <span className={cn('inline-flex items-center gap-0.5', color)}>
          <Icon className="h-3.5 w-3.5" />
          <span className="text-[10px] font-semibold">{Math.round(lvl)}%</span>
        </span>
      ),
    });
  } else if (telemetry.battery !== undefined) {
    items.push({
      key: 'battery-v',
      tip: `Bateria ${telemetry.battery.toFixed(1)}V`,
      node: (
        <span className="inline-flex items-center gap-0.5 text-muted-foreground">
          <Battery className="h-3.5 w-3.5" />
          <span className="text-[10px] font-semibold">{telemetry.battery.toFixed(1)}V</span>
        </span>
      ),
    });
  }

  if (telemetry.charging) {
    items.push({
      key: 'charging',
      tip: 'Carregando',
      node: <Plug className="h-3.5 w-3.5 text-status-running" />,
    });
  }

  if (telemetry.blocked) {
    items.push({
      key: 'blocked',
      tip: 'Motor bloqueado',
      node: <Lock className="h-3.5 w-3.5 text-status-blocked" />,
    });
  }

  if (telemetry.alarm) {
    items.push({
      key: 'alarm',
      tip: `Alarme: ${telemetry.alarm}`,
      node: <AlertTriangle className="h-3.5 w-3.5 text-status-blocked animate-pulse" />,
    });
  }

  if (items.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1', className)}>
      {items.map((it) => (
        <span key={it.key} title={it.tip} className="inline-flex items-center">
          {it.node}
        </span>
      ))}
    </div>
  );
}
